import { buildCollection, buildProperty } from "@firecms/core";
import type { EventCollectionId } from "../workspace/collectionIds";
import type { ActionLogCollectionEntry } from "../types/participantProgress";
import { getAdminT } from "../i18n/getAdminT";
import { getAdminUiLocale } from "../i18n/getAdminUiLocale";
import type { Locale } from "../localization/constants";
import { buildActionTypeEnumValues } from "../properties/actionTypes";
import { buildActionLogDelayField } from "../properties/actionLogDelay";
import { buildTeamsPath } from "./teams";

export type {
  ActionLogCollectionEntry,
  ActionLogEntry,
} from "../types/participantProgress";

/**
 * Matches server actionLog envelope + payload identity fields
 * (`levelId`/`unitId` strings — not contentRelease DocumentReferences).
 */
export const buildParticipantActionLogsCollection = (
  eventId: EventCollectionId,
  locale: Locale = getAdminUiLocale()
) => {
  const t = getAdminT(locale);
  return buildCollection<ActionLogCollectionEntry>({
    name: t("participantProgress.logsCollectionName"),
    singularName: t("participantProgress.logsSingularName"),
    description: t("participantProgress.logsDescription"),
    path: "actionLogs",
    id: "action_logs",
    icon: "History",
    initialSort: ["actionAt", "desc"],
    selectionEnabled: false,
    defaultSize: "s",
    permissions: () => ({
      read: true,
      edit: false,
      create: false,
      delete: false,
    }),
    propertiesOrder: ["actionAt", "delay", "actionType", "payload"],
    additionalFields: [buildActionLogDelayField(locale)],
    properties: {
      actionAt: buildProperty({
        name: "Action at",
        dataType: "date",
        mode: "date_time",
      }),
      writtenAt: buildProperty({
        name: "Written at",
        dataType: "date",
        mode: "date_time",
        hideFromCollection: true,
      }),
      actionType: buildProperty({
        name: "Type",
        dataType: "string",
        enumValues: buildActionTypeEnumValues(locale),
      }),
      payload: buildProperty({
        name: "Payload",
        dataType: "map",
        properties: {
          levelCode: buildProperty({
            name: "Level",
            dataType: "string",
          }),
          unitCode: buildProperty({
            name: "Unit",
            dataType: "string",
          }),
          levelId: buildProperty({
            name: "Level id",
            dataType: "string",
            hideFromCollection: true,
          }),
          unitId: buildProperty({
            name: "Unit id",
            dataType: "string",
            hideFromCollection: true,
          }),
          teamRef: buildProperty({
            name: "Team",
            dataType: "reference",
            path: buildTeamsPath(eventId),
            previewProperties: ["teamNumber"],
            hideFromCollection: true,
          }),
          questionCode: buildProperty({
            name: "Question",
            dataType: "string",
          }),
          optionCode: buildProperty({
            name: "Option",
            dataType: "string",
          }),
          isCorrect: buildProperty({
            name: "Correct",
            dataType: "boolean",
          }),
          role: buildProperty({
            name: "Role",
            dataType: "string",
          }),
          attempt: buildProperty({
            name: "Attempt",
            dataType: "number",
          }),
          score: buildProperty({
            name: "Score",
            dataType: "number",
          }),
          version: buildProperty({
            name: "Version",
            dataType: "string",
          }),
          nick: buildProperty({
            name: "Nick",
            dataType: "string",
          }),
          unitTitle: buildProperty({
            name: "Unit title",
            dataType: "string",
          }),
        },
      }),
    },
  });
};
